import { useLocation } from "react-router-dom";
import { FolderOpen, Plus, Settings } from "lucide-react";

import { PATH_WORKSPACE } from "config";
import { PROJECTS_HOME_NAV_LABELS, isNavPathActive } from "config/navigation";
import { resolveWorkspacePath } from "utils/routing/workspaceRouteUtils";
import { CollapsedTooltip, SidebarIcon, SidebarRowButton, SidebarRowLink } from "./SidebarRow";
import { useTrackNavClick } from "./useTrackNavClick";

interface ProjectsHomeNavProps {
  workspaceId: string;
  canCreateProject: boolean;
  canOpenWorkspaceSettings: boolean;
  onCreateProject: () => void;
}

/** Workspace-level entries on the all-projects page. */
export const ProjectsHomeNav = ({
  workspaceId,
  canCreateProject,
  canOpenWorkspaceSettings,
  onCreateProject,
}: ProjectsHomeNavProps) => {
  const { pathname } = useLocation();
  const trackNavClick = useTrackNavClick();
  const allProjectsHref = resolveWorkspacePath(PATH_WORKSPACE.workspaceProjects, workspaceId);
  const settingsHref = resolveWorkspacePath(PATH_WORKSPACE.workspaceSettings, workspaceId);

  return (
    <div className="flex flex-col gap-px pt-1">
      <CollapsedTooltip label={PROJECTS_HOME_NAV_LABELS.allProjects}>
        <SidebarRowLink
          to={allProjectsHref}
          icon={<SidebarIcon icon={FolderOpen} />}
          label={PROJECTS_HOME_NAV_LABELS.allProjects}
          active={isNavPathActive(pathname, allProjectsHref, { exact: true })}
          onClick={() => trackNavClick({ area: "projects_home", item: "all-projects" })}
        />
      </CollapsedTooltip>
      {canCreateProject ? (
        <CollapsedTooltip label={PROJECTS_HOME_NAV_LABELS.newProject}>
          <SidebarRowButton
            icon={<SidebarIcon icon={Plus} />}
            label={PROJECTS_HOME_NAV_LABELS.newProject}
            onClick={() => {
              trackNavClick({ area: "projects_home", item: "new-project" });
              onCreateProject();
            }}
          />
        </CollapsedTooltip>
      ) : null}
      {canOpenWorkspaceSettings ? (
        <CollapsedTooltip label={PROJECTS_HOME_NAV_LABELS.workspaceSettings}>
          <SidebarRowLink
            to={settingsHref}
            icon={<SidebarIcon icon={Settings} />}
            label={PROJECTS_HOME_NAV_LABELS.workspaceSettings}
            onClick={() => trackNavClick({ area: "projects_home", item: "workspace-settings" })}
          />
        </CollapsedTooltip>
      ) : null}
    </div>
  );
};
